import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";

const ContactItem = ({ icon, label, href, children }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 1,
      py: 0.5,
    }}
  >
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(123, 31, 162, 0.06)",
        flexShrink: 0,
        mt: 0.25,
      }}
    >
      {icon}
    </Box>
    {href ? (
      <Typography
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          color: "primary.main",
          textDecoration: "none",
          fontSize: "0.9rem",
          lineHeight: 1.6,
          fontWeight: 500,
          transition: "color 0.15s ease",
          "&:hover": {
            color: "secondary.main",
            textDecoration: "underline",
          },
        }}
      >
        {children}
      </Typography>
    ) : (
      <Typography
        sx={{
          color: "text.secondary",
          fontSize: "0.9rem",
          lineHeight: 1.6,
        }}
      >
        <Typography
          component="span"
          sx={{ fontWeight: 600, color: "text.primary", fontSize: "inherit" }}
        >
          {label}:{" "}
        </Typography>
        {children}
      </Typography>
    )}
  </Box>
);

const ExpandList = (props) => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%", gap: 2 }}>
      {props.resource.map((obj, idx) => {
        const { resourceTypeName, name, link, phone, address, description, email } = obj;

        if (name === "" && link === "" && phone === "" && address === "") {
          return null;
        }

        return (
          <React.Fragment key={idx}>
            {idx > 0 && (
              <Divider
                sx={{
                  borderColor: "rgba(123, 31, 162, 0.06)",
                }}
              />
            )}
            <Box
              sx={{
                py: 1.5,
                px: { xs: 0, sm: 1 },
                transition: "background-color 0.2s ease",
                borderRadius: 2,
                "&:hover": {
                  backgroundColor: "rgba(123, 31, 162, 0.02)",
                },
              }}
            >
              {/* Resource header: type badge + name */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "flex-start", sm: "center" },
                  gap: 1,
                  mb: 1.5,
                }}
              >
                <Chip
                  label={resourceTypeName}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(123, 31, 162, 0.08)",
                    color: "primary.main",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    height: 26,
                    borderRadius: "8px",
                    border: "1px solid rgba(123, 31, 162, 0.12)",
                  }}
                />
                {name && name.trim() && (
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "0.95rem", sm: "1.05rem" },
                      color: "text.primary",
                      lineHeight: 1.4,
                    }}
                  >
                    {name}
                  </Typography>
                )}
              </Box>

              {/* Description */}
              {description && description.trim() && (
                <Typography
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.875rem",
                    lineHeight: 1.7,
                    mb: 1.5,
                    pl: { xs: 0, sm: 0.5 },
                  }}
                >
                  {description}
                </Typography>
              )}

              {/* Contact details grid */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.75,
                  pl: { xs: 0, sm: 0.5 },
                }}
              >
                {link && link.trim() && (
                  <ContactItem
                    icon={<OpenInNewIcon sx={{ fontSize: 15, color: "primary.main" }} />}
                    href={link}
                  >
                    Visit Website
                  </ContactItem>
                )}
                {phone && phone.toString().trim() && (
                  <ContactItem
                    icon={<PhoneIcon sx={{ fontSize: 15, color: "primary.main" }} />}
                    label="Phone"
                  >
                    {phone}
                  </ContactItem>
                )}
                {email && email.trim() && (
                  <ContactItem
                    icon={<EmailIcon sx={{ fontSize: 15, color: "primary.main" }} />}
                    label="Email"
                  >
                    {email}
                  </ContactItem>
                )}
                {address && address.trim() && (
                  <ContactItem
                    icon={<LocationOnIcon sx={{ fontSize: 15, color: "primary.main" }} />}
                    label="Address"
                  >
                    {address}
                  </ContactItem>
                )}
              </Box>
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default ExpandList;
